import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, ValuationWaterfall } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

export function CapitalPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['capital'],
    queryFn: () => api.capital('acm'),
  });

  const graphOption = useMemo(() => {
    const edges = data?.ownershipEdges ?? [];
    return {
      backgroundColor: 'transparent',
      series: [
        {
          type: 'graph',
          layout: 'force',
          roam: false,
          label: { show: true, color: '#B5C0CE', fontSize: 10 },
          edgeSymbol: ['none', 'arrow'],
          data: [
            { id: 'entity', name: data?.entity?.name ?? 'Entity', symbolSize: 48 },
            ...edges.map((e) => ({
              id: e.edgeId,
              name: e.fromEntityId.replace('ent-', ''),
              symbolSize: 28,
            })),
          ],
          links: edges.map((e) => ({
            source: e.edgeId,
            target: 'entity',
            lineStyle: { color: 'rgba(214,184,106,0.4)' },
          })),
          lineStyle: { color: 'rgba(255,255,255,0.15)' },
          force: { repulsion: 120 },
        },
      ],
    };
  }, [data]);

  if (isLoading) return <PageSkeleton blocks={3} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Capital graph unavailable" />;

  const dims = data.structuralScore?.dimensions ?? [];
  const waterfallItems = [
    { label: 'Base immunity', value: Math.round((dims[0]?.score ?? 0.45) * 100), kind: 'start' as const },
    { label: 'Policy capture', value: Math.round((dims[6]?.score ?? 0.22) * 100), kind: 'positive' as const },
    { label: 'Supply gap', value: -Math.round((1 - (dims[4]?.score ?? 0.55)) * 20), kind: 'negative' as const },
    {
      label: 'Composite',
      value: Math.round((data.structuralScore?.overallScore ?? 0.72) * 100),
      kind: 'total' as const,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Capital" subtitle="TPS factions, ownership, and protected-node analysis." />
      <ExplainActions screenName="Capital" />

      <AnalyticalPanel title="Ownership / TPS graph" tone="feature" eyebrow={data.entity?.name}>
        <ReactECharts option={graphOption} style={{ height: 320 }} opts={{ renderer: 'svg' }} />
        <div className="mt-4 flex flex-wrap gap-2">
          {(data.entity?.classifications ?? []).map((c) => (
            <span key={c} className="rounded-[var(--radius-control)] border border-[var(--color-border-default)] px-2 py-1 text-xs text-[var(--color-atlas-gold)]">
              {c}
            </span>
          ))}
        </div>
      </AnalyticalPanel>

      <AnalyticalPanel
        title="Protected-node waterfall"
        subtitle={`${data.entity?.name} · ${data.structuralScore?.disappearanceTest?.verdict ?? 'protected'}`}
      >
        <ValuationWaterfall items={waterfallItems} />
        <p className="mt-4 text-sm text-[var(--color-text-muted)]">
          {data.structuralScore?.disappearanceTest?.substituteCapacity}
        </p>
      </AnalyticalPanel>
    </div>
  );
}
